/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { copyPropertyIfDefined, fail } from './Common';
import { Forest } from './Forest';
import { TreeView } from './TreeView';
/**
 * An immutable view of a distributed tree.
 * @public
 */
export class RevisionView extends TreeView {
    static fromTree(root, idConverterOrExpensiveValidation, expensiveValidation = false) {
        if (typeof idConverterOrExpensiveValidation === 'object') {
            const rootId = idConverterOrExpensiveValidation.convertToNodeId(root.identifier);
            const treeViewNodes = convertTreeNodesToViewNodes(root, (node) => {
                const identifier = idConverterOrExpensiveValidation.convertToNodeId(node.identifier);
                const viewNode = {
                    definition: node.definition,
                    identifier,
                };
                copyPropertyIfDefined(node, viewNode, 'payload');
                return viewNode;
            });
            return new RevisionView(rootId, Forest.create(expensiveValidation).add(treeViewNodes));
        }
        else {
            return new RevisionView(root.identifier, Forest.create(expensiveValidation).add(convertTreeNodesToViewNodes(root, (node) => {
                const viewNode = {
                    definition: node.definition,
                    identifier: node.identifier,
                };
                copyPropertyIfDefined(node, viewNode, 'payload');
                return viewNode;
            })));
        }
    }
    /** Begin a transaction by generating a mutable `TransactionView` from this view */
    openForTransaction() {
        return new TransactionView(this.root, this.forest);
    }
    equals(view) {
        if (!(view instanceof RevisionView)) {
            return false;
        }
        return this.hasEqualForest(view);
    }
}
/**
 * An view of a distributed tree that is part of an ongoing transaction between `RevisionView`s.
 * @public
 */
export class TransactionView extends TreeView {
    /** Conclude a transaction by generating an immutable `RevisionView` from this view */
    close() {
        return new RevisionView(this.root, this.forest);
    }
    /** Inserts all nodes in a NodeSequence into the view */
    addNodes(sequence) {
        return new TransactionView(this.root, this.forest.add(sequence));
    }
    /** Remove all nodes with the given ids from the view */
    deleteNodes(nodes) {
        return new TransactionView(this.root, this.forest.delete(nodes, true));
    }
    /**
     * Parents a set of detached nodes at a specified place.
     * @param nodesToAttach - the nodes to parent in the specified place. The nodes must already be present in the view.
     * @param place - the location to insert the nodes.
     */
    attachRange(nodesToAttach, place) {
        const { parent, label } = place.trait;
        const index = this.findIndexWithinTrait(place);
        return new TransactionView(this.root, this.forest.attachRangeOfChildren(parent, label, index, nodesToAttach));
    }
    /**
     * Detaches a range of nodes from their parent. The detached nodes remain in the view.
     * @param rangeToDetach - the range of nodes to detach
     */
    detachRange(rangeToDetach) {
        const { start, end } = rangeToDetach;
        const { trait: traitLocation } = start;
        const { parent, label } = traitLocation;
        const startIndex = this.findIndexWithinTrait(start);
        const endIndex = this.findIndexWithinTrait(end);
        const { forest, detached } = this.forest.detachRangeOfChildren(parent, label, startIndex, endIndex);
        return { view: new TransactionView(this.root, forest), detached };
    }
    /**
     * Sets or overwrites a node's value. The node must exist in this view.
     * @param nodeId - the id of the node
     * @param value - the new value
     */
    setNodeValue(nodeId, value) {
        return new TransactionView(this.root, this.forest.setValue(nodeId, value));
    }
    equals(view) {
        if (!(view instanceof TransactionView)) {
            return false;
        }
        return this.hasEqualForest(view);
    }
}
/**
 * Transform an input tree into a list of {@link TreeViewNode}s.
 * @param tree - the input tree
 * @param convert - a conversion function that will run on each node in the input tree to produce the output nodes.
 * Returning undefined means that conversion for the given node was impossible, at which time the entire tree conversion will be aborted
 * and return undefined.
 */
export function convertTreeNodesToViewNodes(root, convert) {
    var _a, _b, _c;
    const convertedRoot = convert(root);
    if (convertedRoot === undefined || root.traits === undefined) {
        return undefined;
    }
    // `convertedRoot` might be the same as `root`, in which case stash the children of `root` before wiping them from `convertedRoot`
    const rootTraits = root === convertedRoot ? { traits: root.traits } : root;
    convertedRoot.traits = new Map();
    const pendingNodes = [{ childIterator: iterateChildren(rootTraits)[Symbol.iterator](), newNode: convertedRoot }];
    const resultNodes = [];
    while (pendingNodes.length > 0) {
        const { childIterator, newNode } = (_a = pendingNodes[pendingNodes.length - 1]) !== null && _a !== void 0 ? _a : fail('Undefined node');
        const { value, done } = childIterator.next();
        if (done === true) {
            resultNodes.push((_c = (_b = pendingNodes.pop()) === null || _b === void 0 ? void 0 : _b.newNode) !== null && _c !== void 0 ? _c : fail('covertTreeNodesToViewNodes incorrectly coordinated parentage'));
        }
        else {
            const [traitLabel, child] = value;
            const convertedChild = convert(child);
            if (convertedChild === undefined) {
                return undefined;
            }
            if (child.traits !== undefined) {
                const childTraits = child === convertedChild ? { traits: child.traits } : child;
                convertedChild.traits = new Map();
                pendingNodes.push({
                    childIterator: iterateChildren(childTraits)[Symbol.iterator](),
                    newNode: convertedChild,
                });
            }
            const newTraits = newNode.traits;
            let newTrait = newTraits.get(traitLabel);
            if (newTrait === undefined) {
                newTrait = [];
                newTraits.set(traitLabel, newTrait);
            }
            newTrait.push(convertedChild.identifier);
        }
    }
    return resultNodes;
}
/**
 * Returns an iterable of the supplied node's traits in a stable order.
 */
export function* iterateChildren(hasTraits) {
    if (hasTraits.traits !== undefined) {
        for (const [label, trait] of Object.entries(hasTraits.traits).sort()) {
            if (trait !== undefined) {
                if (isTreeNodeSequence(trait)) {
                    for (const child of trait) {
                        yield [label, child];
                    }
                }
                else {
                    yield [label, trait];
                }
            }
        }
    }
}
function isTreeNodeSequence(sequence) {
    return Array.isArray(sequence);
}
//# sourceMappingURL=RevisionView.js.map